import React, { useEffect, useMemo, useState } from 'react';
import {
  assignAcademyCourse,
  getAcademyAssignmentOptions
} from '../../../services/academyAdminWriteService';

export function AcademyCourseAssignment({ participant, onClose, onAssigned }) {
  const [courses, setCourses] = useState([]);
  const [openVersionIds, setOpenVersionIds] = useState([]);
  const [courseId, setCourseId] = useState('');
  const [versionId, setVersionId] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let active = true;
    getAcademyAssignmentOptions(participant.id)
      .then((options) => {
        if (!active) return;
        setCourses(options.courses);
        setOpenVersionIds(options.openCourseVersionIds);
      })
      .catch((failure) => {
        if (active) setError(failure.message || 'Godkjente kurs kunne ikke lastes.');
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [participant.id]);

  const selectedCourse = courses.find((course) => course.id === courseId);
  const selectedVersion = useMemo(
    () => selectedCourse?.versions.find((version) => version.id === versionId),
    [selectedCourse, versionId]
  );
  const duplicate = Boolean(versionId && openVersionIds.includes(versionId));

  const changeCourse = (event) => {
    setCourseId(event.target.value);
    setVersionId('');
    setError('');
    setSuccess('');
  };

  const submit = async (event) => {
    event.preventDefault();
    if (!versionId || duplicate) return;
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      await assignAcademyCourse({ learnerId: participant.id, courseVersionId: versionId });
      setOpenVersionIds((current) => [...current, versionId]);
      setSuccess('Påmeldingen er opprettet med status «Venter». Databasens livsløpsregler styrer videre status.');
      await onAssigned();
    } catch (failure) {
      setError(failure.message || 'Kurset kunne ikke tildeles.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-slate-900/40 p-4">
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="assign-course-title"
        className="w-full max-w-xl rounded-2xl bg-white p-6 shadow-2xl"
      >
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h2 id="assign-course-title" className="text-xl font-semibold text-slate-900">Tildel kurs</h2>
            <p className="mt-1 text-sm text-slate-600">{participant.name}</p>
          </div>
          <button type="button" onClick={onClose} className="rounded-lg px-3 py-2 text-sm font-semibold text-slate-600">
            Lukk
          </button>
        </div>

        {loading ? (
          <p role="status" className="rounded-xl bg-slate-50 p-4 text-sm text-slate-600">Laster godkjente kurs …</p>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <label className="block text-sm font-semibold text-slate-700">
              Publisert/godkjent kurs
              <select
                value={courseId}
                onChange={changeCourse}
                required
                className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2.5 font-normal"
              >
                <option value="">Velg kurs</option>
                {courses.map((course) => (
                  <option key={course.id} value={course.id}>{course.title}</option>
                ))}
              </select>
            </label>

            <label className="block text-sm font-semibold text-slate-700">
              Godkjent kursversjon
              <select
                value={versionId}
                onChange={(event) => {
                  setVersionId(event.target.value);
                  setError('');
                  setSuccess('');
                }}
                required
                disabled={!selectedCourse}
                className="mt-2 w-full rounded-xl border border-slate-200 px-3 py-2.5 font-normal disabled:bg-slate-50"
              >
                <option value="">Velg kursversjon</option>
                {selectedCourse?.versions.map((version) => (
                  <option key={version.id} value={version.id}>
                    {version.version} · {version.language_code} · {version.title}
                  </option>
                ))}
              </select>
            </label>

            {selectedVersion && (
              <p className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
                Valgt: {selectedCourse.title}, versjon {selectedVersion.version} ({selectedVersion.language_code}).
              </p>
            )}
            {duplicate && (
              <p role="alert" className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800">
                Deltakeren har allerede en åpen påmelding til denne kursversjonen.
              </p>
            )}
            {courses.length === 0 && !error && (
              <p className="rounded-lg bg-slate-50 p-3 text-sm text-slate-600">
                Ingen godkjente kursversjoner er tilgjengelige for denne deltakeren.
              </p>
            )}
            {error && <p role="alert" className="rounded-lg bg-rose-50 p-3 text-sm text-rose-700">{error}</p>}
            {success && <p role="status" className="rounded-lg bg-emerald-50 p-3 text-sm text-emerald-800">{success}</p>}

            <div className="flex justify-end gap-3 border-t border-slate-100 pt-4">
              <button type="button" onClick={onClose} className="rounded-xl border border-slate-200 px-4 py-2.5 text-sm font-semibold text-slate-700">
                Lukk
              </button>
              <button
                type="submit"
                disabled={saving || !versionId || duplicate}
                className="rounded-xl bg-blue-700 px-4 py-2.5 text-sm font-semibold text-white hover:bg-blue-800 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {saving ? 'Lagrer …' : 'Opprett påmelding'}
              </button>
            </div>
          </form>
        )}
        {loading && error && <p role="alert" className="mt-4 rounded-lg bg-rose-50 p-3 text-sm text-rose-700">{error}</p>}
      </section>
    </div>
  );
}